<template>
  <!--
    Non-series (or a series with no covered dates yet) falls back to the single-date summary; a series
    lists every date its pass covers rather than the first-date placeholder.
  -->
  <event-summary-section
    v-if="!isSeries || !coveredDates.length"
    :event-date="purchase.eventDate"
  />
  <labeled-section v-else :title="eventName" icon="fal fa-calendar-day">
    <v-card variant="outlined" class="pa-4">
      <div
        v-for="date in coveredDates"
        :key="date.eventDateId!"
        style="font-size: 20px"
        class="mb-1"
      >
        <strong>{{ formatDay(date.startLocal!) }}</strong>
        <template v-if="date.endLocal">
          &mdash;
          <span style="white-space: nowrap">
            {{ formatTime(date.startLocal!) }} -
            {{ formatTime(date.endLocal) }}
            <template v-if="date.timezoneAbbreviation"
              >({{ date.timezoneAbbreviation }})</template
            >.
          </span>
        </template>
      </div>

      <div v-if="eventDate" class="text-body-1 pt-1">
        <span
          v-if="
            eventDate.eventBoardingMinutes && eventDate.eventBoardingMinutes > 0
          "
        >
          Please arrive
          <strong>{{ eventDate.eventBoardingMinutes }} minutes</strong> before
          each start
          <template v-if="eventDate.eventLocation">
            at
            <strong
              ><c-display :model="eventDate" for="eventLocation"
            /></strong>
          </template>
          .
        </span>
        <span v-else-if="eventDate.eventLocation">
          Location: <c-display :model="eventDate" for="eventLocation" />.
        </span>
      </div>

      <template v-if="eventDate && eventDate.eventLongDescription">
        <v-divider class="my-2"></v-divider>
        <span
          class="text-pre-wrap"
          v-html="sanitizeHtml(eventDate.eventLongDescription.trim())"
        ></span>
      </template>
    </v-card>
  </labeled-section>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { format } from "date-fns";
import { TicketPurchaseDtoViewModel } from "@/viewmodels.g";
import { sanitizeHtml } from "@common/utils";
import EventSummarySection from "@common/event/event-summary-section.vue";

const props = defineProps<{ purchase: TicketPurchaseDtoViewModel }>();

const isSeries = computed(() => props.purchase.eventSeriesId != null);
const eventDate = computed(() => props.purchase.eventDate);
const eventName = computed(
  () => props.purchase.eventDate?.eventName ?? undefined,
);
const coveredDates = computed(() => props.purchase.coveredSeriesDates ?? []);

// startLocal/endLocal are already venue-localized server-side, so format them directly instead of
// re-deriving the timezone on the client (matching the single-date block).
function formatDay(date: Date) {
  return format(date, "EEEE, MMMM do, yyyy");
}
function formatTime(date: Date) {
  return format(date, "h:mm a");
}
</script>
